import { LoginGuard } from '@components/layout/LoginGuard';
import { ReadOnlyLayout } from '@components/layout/ReadOnlyLayout/ReadOnlyLayout';
import { ApiProvider } from '@data-access';
import { CookieHelper } from '@helpers';
import { I18nProvider } from '@i18n';
import { AuthorizationProvider } from '@providers';
import { userStore, viewSettingsStore } from '@stores';
import { featureFlagsStore } from '@stores/featureFlags';
import { notionStore } from '@stores/notion.store';
import { settingsStore } from '@stores/settings.store';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Title, Legend } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { Poppins } from 'next/font/google';
import Head from 'next/head';
import { Router, useRouter } from 'next/router';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-datepicker/dist/react-datepicker.css';
import '../assets/scss/index.scss';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend,
  ChartDataLabels,
  TreemapController,
  TreemapElement,
  MatrixController,
  MatrixElement,
);

const popins = Poppins({
  weight: ['300', '400', '500', '600', '700', '800'],
  subsets: ['latin'],
});

// Initialize PostHog
if (typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
    defaults: '2025-11-30',
    // Enable session recording for detailed session duration tracking
    session_recording: {
      recordCrossOriginIframes: false,
    },
    // Track page views automatically
    capture_pageview: true,
    debug: false,
  });
}

const App = observer(({ Component, pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  const isReadOnly = router.asPath.includes('mode=read_only');
  const getLayout = isReadOnly
    ? (page: ReactElement) => <ReadOnlyLayout>{page}</ReadOnlyLayout>
    : Component.getLayout ?? ((page: ReactElement) => page);

  const { setValues } = notionStore;
  const { setViewSetting } = viewSettingsStore;
  const { initializeRiskLimits } = settingsStore;
  const { initializeFeatureFlags } = featureFlagsStore;
  const isAuthorized = !!userStore.user.id;

  const [posthogReady, setPosthogReady] = useState(false);

  useEffect(() => {
    // Always let the app continue even if PostHog is blocked
    try {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        defaults: '2025-11-30',
        session_recording: { recordCrossOriginIframes: false },
        capture_pageview: false, // important: you already do manual route tracking below
        debug: false,
      });
    } catch {
      // swallow
    } finally {
      setPosthogReady(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      setValues();
      setViewSetting();
      initializeRiskLimits();
    }
    // Before the user resolves, `isAuthorized` is still false on an authenticated load —
    // fetching here would only be thrown away and refetched once the user arrives.
    if (isAuthorized || !CookieHelper.get('access_token')) {
      initializeFeatureFlags();
    }
  }, [isAuthorized, setValues, setViewSetting, initializeRiskLimits, initializeFeatureFlags]);

  useEffect(() => {
    // Track page views with page info (safe-guarded)
    const handleRouteChange = (path: string) => {
      try {
        posthog?.capture?.('$pageview', {
          page_path: path,
          page_title: document.title,
        });
      } catch (e) {
        // Silently ignore PostHog errors to avoid blocking the app
      }
    };
    Router.events.on('routeChangeComplete', handleRouteChange);
    return () => Router.events.off('routeChangeComplete', handleRouteChange);
  }, []);

  useEffect(() => {
    if (isAuthorized && userStore.user.id) {
      try {
        const tenantId = typeof window !== 'undefined' ? window.location.hostname : '';
        posthog?.identify?.(String(userStore.user.id), {
          tenant_id: tenantId,
          tenant_url: tenantId,
          email: userStore.user.email,
        });
      } catch (e) {
        // Silently ignore PostHog errors to avoid blocking the app
      }
    }
  }, [isAuthorized]);

  // Don’t mount guard/provider until posthog init attempt completed
  if (!posthogReady) return null;

  return (
    <>
      <Head>
        <link
          rel="icon"
          href="https://uploads-ssl.webflow.com/636570213cdbb0358d0fc5bc/636bd528c38dd1ac91c9946a_Favicon.png"
        />
        <title>Calvin</title>
        <meta
          name="password-manager-skip-form-detection"
          content="true"
        />
      </Head>

      <I18nProvider>
        <ApiProvider>
          <AuthorizationProvider>
            <LoginGuard>
              <PostHogProvider client={posthog}>
                <div className={clsx(popins.className)}>{getLayout(<Component {...pageProps} />)}</div>
              </PostHogProvider>
            </LoginGuard>
            <ToastContainer
              containerId="toast-notifications"
              autoClose={false}
              position="top-right"
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </AuthorizationProvider>
        </ApiProvider>
      </I18nProvider>
    </>
  );
});

export default App;
